import { cn } from '@repo/tailwind-config/tailwind.functions'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { FileTypeEnum } from '@repo/toolkit/types'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { DisplayImage } from '@repo/ui/file-input'
import {
  ArrowLeftRight,
  CheckMarkIcon,
  DeleteIcon,
  InvoiceIcon,
  PdfIcon,
  PlusIcon,
  PrintIcon,
  UploadIcon
} from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../api/apis.config'
import { deleteCenterOlPayments } from '../../api/center-ol-payments/delete-center-ol-payments'
import { getCenterOlPaymentTotalAmountsByCurrency } from '../../api/center-ol-payments/get-center-ol-payments-total-amounts-by-currency'
import { validateCenterOlPayment } from '../../api/center-ol-payments/validate-create-center-ol-payment'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import {
  canValidateCenterOlPayment,
  CenterOlPaymentStatusEnum,
  centerOlPaymentStatusEnumMapping,
  getCenterOlPaymentStatus
} from '../../lib/types/enums/center-ol-payment/center-ol-payment-status.enum'
import { centerOlPaymentTypeMapping } from '../../lib/types/enums/center-ol-payment/center-ol-payment-type.enum'
import {
  CenterOlPayment,
  CenterOlPaymentFilters
} from '../../lib/types/models/center-ol-payment/center-ol-payment'
import { FileType } from '../../lib/types/models/file/file'
import {
  transformCenterOlPaymentFiltersToUrl,
  transformUrlToCenterOlPaymentFilters
} from '../../lib/utils/filters-mapping/center-ol-payment'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getOlName } from '../ols/center/utils/functions'
import { CenterOlPaymentsFilter } from './center-ol-payments-filter'

export const centerOlPaymentInitialFilters: CenterOlPaymentFilters = {}

export function CenterOlPaymentsListPage() {
  const { t } = useTranslate()
  const { navigate, navigateWithEvent } = useNavigator()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [search, setSearch] = useState<string>()

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CenterOlPaymentFilters>({
      initialFilters: centerOlPaymentInitialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformCenterOlPaymentFiltersToUrl,
      transformUrlToFilter: transformUrlToCenterOlPaymentFilters
    })

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'delete' | 'validate',
    CenterOlPayment[]
  >(['filters', 'delete', 'validate'])

  const fileModalManager = useModalManager<'view-invoice', FileType>(['view-invoice'])
  const pdfModalManager = useModalManager<'view-pdf', string>(['view-pdf'])

  const tableRef = React.useRef<DynamicTableRef<CenterOlPayment>>(null)
  const { downloadDocuments } = useDownloadDocuments()

  const validateCenterOlPaymentMutation = useMutation({
    mutationFn: validateCenterOlPayment,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Virement(s) validé(s) avec succès')
      closeModel('validate')
    }
  })

  const deleteCenterOlPaymentsMutation = useMutation({
    mutationFn: deleteCenterOlPayments,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Virement(s) supprimé(s) avec succès')
      closeModel('delete')
    }
  })

  const centerOlPaymentsTotalAmountsByCurrencyQuery = useQuery({
    queryKey: ['center-ol-payments-total-amounts-by-currency', selectedFilter, search],
    queryFn: () => getCenterOlPaymentTotalAmountsByCurrency({ filter: selectedFilter, search })
  })

  async function handleViewBankTransferOrderPdf(centerOlPaymentId: number) {
    const { fileLink } = await downloadDocuments(
      {
        endpoint: constructApiUrl(API.centerOlPayments.generateBankTransferOrder.url, {
          id: centerOlPaymentId
        })
      },
      { automaticallyDownload: false }
    )

    if (fileLink) pdfModalManager.toggleModal('view-pdf', fileLink)
  }

  const columns: Column<CenterOlPayment>[] = [
    {
      accessorKey: 'ol',
      header: 'Prestataire',
      cell: ({ row }) => (
        <Typography variant="secondary-body" className="whitespace-nowrap">
          {getOlName(row.original.ol)}
        </Typography>
      )
    },
    {
      accessorKey: 'label',
      header: 'Libellé',
      cell: ({ row }) => (
        <Typography variant="secondary-body" className="whitespace-nowrap">
          {valueRender(row.original.label)}
        </Typography>
      )
    },
    {
      accessorKey: 'country',
      header: 'Pays',
      cell: ({ row }) => <CountryFlag code={row.original.country?.code} />
    },
    {
      accessorKey: 'periods',
      header: 'Périodes',
      cell: ({ row }) => {
        const periods = row.original.periods
        if (!periods || periods.length === 0) return '-'
        return (
          <div className="flex gap-2 items-center flex-wrap py-2">
            {periods?.map((p) => (
              <PeriodBadge key={p.id} period={p} />
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'type',
      header: 'Type de virement',
      cell: ({ row }) => (
        <Badge
          variant={centerOlPaymentTypeMapping[row.original.type].variant}
          text={centerOlPaymentTypeMapping[row.original.type].label}
        />
      )
    },
    {
      accessorKey: 'amount',
      header: 'Montant',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          variant="primaryLight"
          text={NumberFormatter.formatCurrency(row.original.amount, row.original.currency)}
        />
      )
    },
    {
      accessorKey: 'dateOfPayment',
      header: 'Date du virement',
      enableSorting: true,
      cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.dateOfPayment)} />
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      cell: ({ row }) => {
        const { color, label } = getCenterOlPaymentStatus(row.original)
        return <Badge variant={color} text={label} />
      }
    },
    {
      accessorKey: 'files',
      header: 'Facture(s)',
      cell: ({ row }) => {
        const files = row.original.files
        if (!files || files.length === 0) return <div className="text-center">-</div>
        return (
          <div className="flex gap-2 flex-wrap justify-center py-2 w-24">
            {files.map((file, index) => (
              <ActionButton
                key={index}
                variant={'ghost'}
                icon={InvoiceIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  fileModalManager.toggleModal('view-invoice', file)
                }}
              />
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: '',
      header: 'Voir le PDF',
      cell: ({ row }) => (
        <ActionButton
          variant="ghost"
          icon={PdfIcon}
          onClick={async (e) => {
            e.stopPropagation()
            await handleViewBankTransferOrderPdf(row.original.id)
          }}
        />
      )
    },
    {
      accessorKey: '',
      header: 'Valider le paiement',
      cell: ({ row }) => {
        if (row.original.status === CenterOlPaymentStatusEnum.VALIDATED) {
          const { color, label } = centerOlPaymentStatusEnumMapping[row.original.status]
          return <Badge variant={color} text={t(label)} />
        }

        return (
          <Button
            icon={CheckMarkIcon}
            label="Valider"
            variant="outline"
            className="border-grey-light"
            disabled={!canValidateCenterOlPayment(row.original)}
            loading={validateCenterOlPaymentMutation.isPending}
            onClick={(e) => {
              e.stopPropagation()
              toggleModal('validate', [row.original])
            }}
          />
        )
      }
    },
    {
      accessorKey: '',
      header: ' ',
      cell: ({ row }) => (
        <ActionButton
          variant="danger"
          icon={DeleteIcon}
          className={'mr-2'}
          disabled={!!row.original.validatedAt}
          onClick={(e) => {
            e.stopPropagation()
            toggleModal('delete', [row.original])
          }}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Liste des virements" icon={ArrowLeftRight} />
      <div className={'h-4'} />

      <DynamicDataTable<CenterOlPayment, CenterOlPaymentFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Tous les virements"
        displayOrder
        deleteMessage="Etes-vous sûr de vouloir supprimer ce virement ?"
        columns={columns}
        selectable
        permissions={{
          delete: true,
          disableDelete: (data) => data.some((item) => !!item.validatedAt)
        }}
        allowSearchParam
        onSearchChange={setSearch}
        endpoint={API.centerOlPayments.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onRowClick={(account, event) =>
          navigateWithEvent(
            Paths.CENTER_OL_PAYMENTS_UPDATE_PAGE.replace(':id', account.id.toString()),
            event
          )
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Créer un virement',
            fn: () => navigate(Paths.CENTER_OL_PAYMENTS_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: CheckMarkIcon,
            variant: 'ghost',
            label: 'Valider les virements',
            disabled: (rows) => !rows.every(canValidateCenterOlPayment),
            fn: (data) => toggleModal('validate', data)
          },
          {
            type: 'multiple',
            icon: PrintIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Imprimer les factures',
            fn: async (data) => {
              await downloadDocuments({
                ids: data.map(({ id }) => id),
                endpoint: API.centerOlPayments.downloadInvoices.url
              })

              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter fichier',
            fn: async (data) => {
              await downloadDocuments({
                ids: data.map(({ id }) => id),
                endpoint: API.centerOlPayments.generateXml.url
              })

              tableRef.current?.refetch()
            }
          }
        ]}
        footerclassName="bg-white-hover"
        footerCells={[
          {
            accessorKey: 'id',
            colSpan: 3,
            value: (
              <Typography variant="tag" className="whitespace-nowrap">
                Montant total des transferts par devise
              </Typography>
            )
          },
          {
            accessorKey: 'amount',
            value: (
              <div className="flex flex-col gap-2 py-2">
                {centerOlPaymentsTotalAmountsByCurrencyQuery.data?.map((item) => (
                  <Badge
                    key={item.currency}
                    variant="orange"
                    text={NumberFormatter.formatCurrency(item.totalAmount, item.currency)}
                  />
                ))}
              </div>
            )
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <CenterOlPaymentsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <>
          <Modal
            icon={DeleteIcon}
            isLoading={deleteCenterOlPaymentsMutation.isPending}
            variant="destructive"
            isOpen={modalState.delete}
            title="Etes-vous sûr de vouloir supprimer ce virement ?"
            onCancel={() => closeModel('delete')}
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Annuler',
              onConfirm: async () =>
                await deleteCenterOlPaymentsMutation.mutateAsync(selectedData.map(({ id }) => id))
            }}
          />

          <Modal
            icon={CheckMarkIcon}
            isLoading={validateCenterOlPaymentMutation.isPending}
            variant="confirmation"
            isOpen={modalState.validate}
            title="Êtes-vous sûr de vouloir valider ce virement ?"
            onCancel={() => closeModel('validate')}
            actionButtonsOptions={{
              confirmText: 'Valider',
              cancelText: 'Annuler',
              onConfirm: async () =>
                await validateCenterOlPaymentMutation.mutateAsync(selectedData.map(({ id }) => id))
            }}
          />
        </>
      )}

      {fileModalManager.selectedData && (
        <Modal
          icon={PdfIcon}
          hideActions
          title={fileModalManager.selectedData.name}
          size={'large'}
          isOpen={fileModalManager.modalState['view-invoice']}
          onCancel={() => fileModalManager.closeModel('view-invoice')}
        >
          <DisplayImage
            allowAll
            type={fileModalManager.selectedData.type}
            src={fileModalManager.selectedData.path}
            className={'h-[70vh] w-full object-contain relative z-10 overflow-auto'}
          />{' '}
        </Modal>
      )}

      {pdfModalManager.selectedData && (
        <Modal
          hideActions
          size="large"
          className="!h-[95vh] max-h-[95vh]"
          icon={PdfIcon}
          isOpen={pdfModalManager.modalState['view-pdf']}
          onCancel={() => pdfModalManager.closeModel('view-pdf')}
        >
          <div className={cn(MODAL_CONTENT_CLASSNAME, 'flex flex-col gap-4')}>
            <DisplayImage
              allowAll
              type={FileTypeEnum.PDF}
              src={pdfModalManager.selectedData}
              className="h-[75dvh]"
            />

            <div className="flex flex-row gap-2 justify-end">
              <Button
                variant={'ghost'}
                label={t('enrollment.profile.pdf.close')}
                onClick={() => pdfModalManager.closeModel('view-pdf')}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
